// TODO: (Later iteration) autofill & disable school fields if voucher got entered

import { Text, Button, VStack, Flex, Center, HStack, Link, Box } from '@chakra-ui/react';
import { ChangeEvent, ReactElement, useEffect, useRef, useState } from 'react';
import Joi from 'joi';
import {
  AutocompleteField,
  RHFFormInput,
  SelectField,
  useAutocompleteConfirmation,
} from 'components/common/Form';
import { useSchoolApi } from 'hooks/api/backend/useSchoolsApi';
import { FieldError } from 'react-hook-form';

// assets
import { EducationStatus, School, VoucherCategory } from '@novaheal/types';
import { caughtErrorGlobally } from 'utils/axios';
import { AxiosError } from 'axios';
import { CalendarIcon, CloseIcon, EducationIcon, SchoolIcon } from 'assets/icons/v2';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { ToastVariant } from 'types/Toast';
import { errorMessage } from 'components/common/Form/ErrorMessages';
import { useFirebase } from 'hooks/useFirebase';
import { useModalService } from 'hooks/useModalService';
import { ModalVariant } from 'types/Modal';
import { useWelcomePostStep } from 'hooks/api/backend/welcome/useWelcomeStep';
import { EduPrefillData, UpdateEducationDataDto } from 'types/WelcomeScreen';
import { ErrorStatusType } from 'types/StatusTypes';
import { toastContent } from 'config/toast';

import { formattedDateWithMonthName } from 'utils/formattedDate';
import {
  EXAMINATION_MONTH_OPTIONS,
  examinationDateUnknownSchema,
  examinationMonthSchema,
  examinationYearSchema,
  getPrefilledExaminationDateKey,
  validatePrefilledExaminationDateFields,
  triggerExaminationDateFields,
  revalidateExaminationAfterStatusChange,
  pickSelectFieldRegistration,
  EDUCATION_STATUS_OPTIONS,
} from 'utils/education';
import * as Sentry from '@sentry/react';
import { useUser } from 'utils/auth';
import { Form, RHFCheckboxField } from 'components/common/Form/v2';
import { WelcomeScreenOptions } from './Welcome';

type OrganizationInput = {
  name: string;
  id: string | null;
  educationStatus: EducationStatus;
  examinationMonth?: number | null;
  examinationYear?: number | null;
  examinationDateUnknown: boolean;
};

type Props = WelcomeScreenOptions & {
  prefilledData?: {
    educationStatus: EducationStatus | null;
    school?: {
      isDisabled: boolean;
      value?: string;
      id?: string;
    };
    userExamination: {
      id: string;
      month: number;
      year: number;
      userId: string;
    } | null;
  };
};

const schema = Joi.object({
  name: Joi.string().required().messages({
    'string.empty': errorMessage.empty,
  }),
  id: Joi.string().allow(null).min(2),
  educationStatus: Joi.string().required().messages({
    'string.empty': errorMessage.empty,
  }),
  examinationDateUnknown: examinationDateUnknownSchema,
  examinationMonth: examinationMonthSchema,
  examinationYear: examinationYearSchema,
});

export const EducationInfo = (options: Props): ReactElement => {
  const { onContinue, stepData } = options;
  const { data: user } = useUser();
  const prefilledData = stepData.currentData?.prefillData as EduPrefillData;
  const { getAutoCompleteItems } = useSchoolApi();
  const toast = useNovaToast();
  const [isLoading, setLoading] = useState(false);
  const { logFirebaseUserData } = useFirebase();
  const modalService = useModalService();
  const { mutateAsync: updateWelcomeStep } = useWelcomePostStep();
  const shouldShowExaminationFields = (selectedEduOption: string): boolean => {
    const fieldsToShowExamination = ['FIRST', 'SECOND', 'LAST'];
    return fieldsToShowExamination.includes(selectedEduOption);
  };
  const [voucherSchoolData, setVoucherSchoolData] = useState<School | null>(null);

  const prefilledSchoolId = prefilledData?.school?.id ?? voucherSchoolData?.id;
  const prefilledSchoolName = prefilledData?.school?.value ?? voucherSchoolData?.name;
  const { isConfirmed: isSchoolConfirmed, onConfirmedChange: onSchoolConfirmedChange } =
    useAutocompleteConfirmation({
      id: prefilledSchoolId,
      name: prefilledSchoolName,
    });

  const { voucher } = user || {};
  const toastShownRef = useRef<boolean>(false);
  const isExaminationUnknownPrefill = prefilledData?.examinationDateUnknown === true;

  useEffect(() => {
    if (voucher?.id && !toastShownRef.current) {
      const toastObject =
        voucher?.category === VoucherCategory.B2B
          ? toastContent.voucher.onboardingLicenseActivated
          : toastContent.voucher.onboardingCodeActivated;

      // TODO: Remove this toast when notification system is implemented
      toast.show(toastObject);

      toastShownRef.current = true;
    }
  }, [voucher, toast, toastShownRef]);

  const onVoucherActivation = (schoolData?: School): void => {
    if (schoolData)
      setVoucherSchoolData({
        name: schoolData.name,
        id: schoolData.id,
      });
  };

  const handleVoucherClick = (): void => {
    modalService.show(ModalVariant.VOUCHER, {
      onSuccess: onVoucherActivation,
    });
  };

  const handleSubmit = async (values: OrganizationInput): Promise<void> => {
    setLoading(true);
    try {
      const showExamination = shouldShowExaminationFields(values.educationStatus);
      const isUnknown = showExamination && values.examinationDateUnknown;

      const onboardingData = await updateWelcomeStep({
        stepIdentifier: stepData.currentStepIdentifier,
        totalSteps: stepData.totalSteps,
        step: stepData.currentStep,
        data: {
          b2cSchoolData: {
            id: values.id,
            name: values.name.trim(),
          },
          educationStatus: values.educationStatus,
          ...(showExamination && {
            examinationDateUnknown: isUnknown,
            examination: isUnknown
              ? null
              : {
                  month: values.examinationMonth as number,
                  year: values.examinationYear as number,
                },
          }),
        } as UpdateEducationDataDto,
      });
      logFirebaseUserData({
        userSchool: values.name,
        userEducationStatus: values.educationStatus,
      });
      onContinue(onboardingData);
    } catch (error) {
      if (caughtErrorGlobally(error as AxiosError)) {
        toast.show(toastContent.error[ErrorStatusType.SERVER_ERROR]);
        return;
      }
      toast.show({
        variant: ToastVariant.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const getActiveVoucherText = (): string => {
    if (!prefilledData.voucher) return '';

    const { category, validUntil } = prefilledData.voucher;

    const voucherExpirationDate = formattedDateWithMonthName(validUntil);
    if (category === VoucherCategory.GIFT) {
      if (voucherExpirationDate) {
        return `Du hast einen Gutscheincode eingelöst, der bis zum ${voucherExpirationDate} gilt. Viel Spaß beim Lernen!`;
      }
      return `Du hast einen Gutscheincode eingelöst, der unbegrenzt gilt. Viel Spaß beim Lernen!`;
    }
    if (category === VoucherCategory.B2B) {
      return `Novaheal wird von deinem Arbeitgeber bis zum ${voucherExpirationDate} für dich bezahlt. Viel Spaß!`;
    }
    if (category === VoucherCategory.AFFILIATE) {
      return `Du hast einen aktiven Gutscheincode eingelöst und kannst Novaheal kostenlos bis zum ${voucherExpirationDate} nutzen. Viel Spaß beim Lernen!`;
    }
    if (category === VoucherCategory.AMBOSS) {
      return `Du hast einen Gutscheincode eingelöst und kannst Novaheal kostenlos nutzen. Viel Spaß beim Lernen!`;
    }
    Sentry.captureMessage(
      `Voucher type not determined for voucher ${user?.voucher?.id}, used by user ${user?.id}`
    );
    return '';
  };

  const formOptions = {
    defaultValues: {
      educationStatus: prefilledData?.educationStatus ?? undefined,
      examinationMonth: isExaminationUnknownPrefill
        ? null
        : (prefilledData?.userExamination?.month ?? null),
      examinationYear: isExaminationUnknownPrefill
        ? null
        : (prefilledData?.userExamination?.year ?? null),
      examinationDateUnknown: isExaminationUnknownPrefill,
    },
  };

  return (
    <Flex width="100%" direction="column" gap="0" alignItems="center">
      <Text as="h1" textStyle="h2.medium" color="text.primary" mb="25px">
        Gib uns noch ein paar Infos, damit wir dir passende Inhalte, Tipps und Erinnerungen schicken können.
      </Text>
      <Box width="100%">
        <Form<OrganizationInput, typeof schema>
          onSubmit={handleSubmit}
          schema={schema}
          options={formOptions}
          autoComplete="off"
          onMount={({ trigger }) =>
            validatePrefilledExaminationDateFields(formOptions.defaultValues, trigger)
          }
          onMountKey={getPrefilledExaminationDateKey(formOptions.defaultValues)}
        >
          {({ register, formState, setValue, getValues, watch, resetField, trigger }) => {
            const { isValid } = formState;
            const isExaminationUnknown = !!watch('examinationDateUnknown');
            const showExamination = shouldShowExaminationFields(watch('educationStatus') ?? '');
            const examinationErrorMessage =
              formState.errors.examinationYear?.type === 'custom'
                ? formState.errors.examinationYear.message
                : (formState.errors.examinationMonth?.message ??
                  formState.errors.examinationYear?.message);

            const clearExaminationFields = (): void => {
              setValue('examinationMonth', null, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: false,
              });
              setValue('examinationYear', null, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: false,
              });
            };

            return (
              <>
                <Flex direction="column" gap="l">
                  <AutocompleteField
                    getAutocompleteItems={getAutoCompleteItems}
                    initialName={
                      prefilledData?.school?.value ?? voucherSchoolData?.name ?? undefined
                    }
                    initialId={prefilledData?.school?.id ?? voucherSchoolData?.id ?? undefined}
                    disabled={prefilledData?.school?.isDisabled || !!voucherSchoolData?.id}
                    onConfirmedChange={onSchoolConfirmedChange}
                    registrationName="name"
                    error={formState.errors.name as FieldError}
                    idName="id"
                    register={register}
                    setValue={setValue}
                    getValues={getValues}
                    placeholder="Pflegeschule o. Institution"
                    leftElement={
                      <SchoolIcon
                        color={watch('name') ? 'icon.accent' : 'icon.quaternary'}
                        boxSize="18px"
                      />
                    }
                    zIndex={1}
                    rightElement={
                      !(prefilledData?.school?.isDisabled || !!voucherSchoolData?.id) &&
                      watch('name') ? (
                        <Button
                          ml="m"
                          bg="transparent"
                          _hover={{ backgroundColor: 'none' }}
                          _active={{ backgroundColor: 'none' }}
                          _focus={{ outline: 'none' }}
                          onClick={() => {
                            resetField('name');
                            resetField('id');
                            onSchoolConfirmedChange(false);
                          }}
                          variant="tertiary"
                        >
                          <Center bg="text.quaternary" rounded="full" boxSize="16px">
                            <CloseIcon
                              color="icon.onAccent"
                              mt="3px"
                              w="10px"
                              m={0}
                              boxSize="18px"
                            />
                          </Center>
                        </Button>
                      ) : undefined
                    }
                  />
                  <SelectField
                    options={EDUCATION_STATUS_OPTIONS}
                    placeholder="Aktueller Status"
                    error={formState.errors.educationStatus as FieldError}
                    registration={pickSelectFieldRegistration(register('educationStatus'))}
                    leftElement={
                      <EducationIcon
                        color={watch('educationStatus') ? 'icon.accent' : 'icon.quaternary'}
                        boxSize="18px"
                      />
                    }
                    defaultValue={prefilledData?.educationStatus ?? undefined}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                      revalidateExaminationAfterStatusChange(e.target.value as EducationStatus, {
                        setValue,
                        trigger,
                        shouldShowExaminationFields,
                        clearExaminationFields,
                      }).catch(() => undefined);
                    }}
                  />

                  {showExamination && (
                    <VStack width="100%" alignItems="flex-start" gap="15px">
                      <VStack alignItems="flex-start" gap="2xs">
                        <Text textStyle="body.m.regular" color="text.secondary" textAlign="left">
                          Weißt du schon, wann deine staatliche Abschlussprüfung ist?
                        </Text>
                        <Text textStyle="body.xs.regular" color="text.tertiary" textAlign="left">
                          Damit können wir dich noch besser auf dem Weg zu begleiten.
                        </Text>
                      </VStack>
                      <VStack width="100%" alignItems="flex-start" gap="xs">
                        <HStack
                          key={isExaminationUnknown ? 'exam-unknown' : 'exam-known'}
                          alignItems="flex-start"
                          justifyContent="flex-start"
                          width="100%"
                          gap="l"
                        >
                          <SelectField
                            options={EXAMINATION_MONTH_OPTIONS}
                            placeholder="Monat"
                            registration={register('examinationMonth')}
                            defaultValue={
                              isExaminationUnknown
                                ? undefined
                                : (prefilledData?.userExamination?.month ?? undefined)
                            }
                            disabled={isExaminationUnknown}
                            leftElement={
                              <CalendarIcon
                                color={
                                  watch('examinationMonth') ? 'icon.accent' : 'icon.quaternary'
                                }
                                boxSize="18px"
                              />
                            }
                            onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                              setValue('examinationMonth', Number(e.target.value), {
                                shouldDirty: true,
                                shouldTouch: true,
                                shouldValidate: false,
                              });
                              triggerExaminationDateFields(trigger).catch(() => undefined);
                            }}
                          />
                          <RHFFormInput
                            placeholder="Jahr"
                            name="examinationYear"
                            disabled={isExaminationUnknown}
                            error={{ type: 'suppressed', message: undefined } as FieldError}
                            leftElement={<CalendarIcon boxSize="18px" />}
                          />
                        </HStack>
                        {examinationErrorMessage && (
                          <Text
                            width="100%"
                            textStyle="body.xs.regular"
                            color="text.error"
                            textAlign="left"
                          >
                            {String(examinationErrorMessage)}
                          </Text>
                        )}
                      </VStack>
                      <RHFCheckboxField<OrganizationInput>
                        name="examinationDateUnknown"
                        onChange={(checked) => {
                          if (checked) {
                            clearExaminationFields();
                          }
                          triggerExaminationDateFields(trigger).catch(() => undefined);
                        }}
                        labelElement={
                          <Text textStyle="body.s.regular">
                            Ich kenne meinen Termin noch nicht – ich ergänze ihn später.
                          </Text>
                        }
                      />
                    </VStack>
                  )}
                  {prefilledData?.school?.isDisabled ? (
                    <Text textStyle="body.s.regular">{getActiveVoucherText()}</Text>
                  ) : (
                    <Text textStyle="body.s.regular">
                      Du hast einen Zugangscode für Novaheal erhalten? Hier kannst du{' '}
                      <Link color="text.info" onClick={handleVoucherClick} display="inline">
                        deinen Zugangscode eingeben
                      </Link>
                      .
                    </Text>
                  )}
                </Flex>

                <Button
                  translate="no"
                  variant="primary"
                  type="submit"
                  loading={isLoading}
                  disabled={!isValid || !isSchoolConfirmed}
                  loadingText="Speichern"
                  mt="40px"
                  w="full"
                  py="10px"
                >
                  Weiter
                </Button>
              </>
            );
          }}
        </Form>
      </Box>
    </Flex>
  );
};
